//@flow
import {
    validateFuelType,
    validateFleetType,
    validateVehicleType,
    validateUnloadingSide,
    MasterVehicleModel
} from 'common/models/internal/MasterVehicleModel';
import EntityImportDataHelper from 'common/entity/helpers/EntityImportDataHelper';
import DataUtils from 'common/utils/DataUtils';
import { TransporterId } from 'common/models/internal/TransporterId';

import I18nHelper from 'common/helpers/I18nHelper';
import { VolumeLimitUnitEnum } from 'common/models/internal/VolumeLimit';
import { EntityImportHelper } from 'common/entity/helpers/EntityImportHelper';
import * as Immutable from 'immutable';
import { RiderPersonaLimits } from 'common/models/internal/RiderPersonaLimits';
import { UnitEnum } from 'common/models/internal/RiderPersonaResourceLimit';

const translationsNamespace = ['entityManagement', 'validations'];
const nsTranslate = I18nHelper.getTranslate(translationsNamespace);

const SECONDS = 'SECONDS';
const KG = 'KG';

export class VehicleModelImportHelper extends EntityImportDataHelper {
    static validateFuelType({ entity }) {
        const { fuelType } = entity;
        return validateFuelType(fuelType);
    }

    static validateFleetType({ entity }) {
        const { fleetType } = entity;
        return validateFleetType(fleetType);
    }

    static validateVehicleType({ entity }) {
        const { vehicleType } = entity;
        return validateVehicleType(vehicleType);
    }

    static validateHigherThresh({ entity, header }) {
        const accessor = header?.accessor?.slice(0, 2);

        const resource = entity?.getIn(accessor);
        return resource?.validateHigherThresh?.() || null;
    }

    static validateDimensions({ entity, header }) {
        const [key] = header.id.split('.');
        const { dimensions } = entity;
        return dimensions.getValidationMessage(key);
    }

    static validateVolume({ entity }) {
        return entity.validateVolume();
    }

    static validateUnloadingSide({ entity }) {
        const { unloadingSide } = entity;
        return validateUnloadingSide(unloadingSide);
    }

    static validateMaxAdditionalTrips({ entity }) {
        return MasterVehicleModel._validateMaxAdditionalTrips(entity);
    }

    static validateLimitMultiTrip({ entity }) {
        return MasterVehicleModel._validateLimitMultiTrip(entity);
    }

    static validateMaxPayloadWeight({ entity }) {
        if (
            !DataUtils.isEmpty(entity.limits?.weight?.higherThresh) &&
            !DataUtils.isEmpty(entity.maxPayloadWeight) &&
            Number(entity.limits.weight.higherThresh) >
                Number(entity.maxPayloadWeight)
        ) {
            return nsTranslate(
                'validations:vehicleModelValidationErrors.maxPayloadWeightLessThanMaxWeight'
            );
        }
    }

    /**
     * This method takes `transporterId` and @returns `TransporterId` object of `Immutable.Map`
     * Used for edit masters table
     * @static
     * @memberof EntityImportDataHelper
     */
    static serializeTransporterId = transporterId =>
        TransporterId.fromJS({ transporterId });

    /**
     * This method takes object arg as accessor and returns top level accessor for transporterId
     *
     * @static
     * @memberof VehicleImportHelper
     */
    static getTransporterIdAccessor = ({ accessor }) => accessor.pop();

    static validateVolumeUnit = ({
        entity
    }: {
        entity: MasterVehicleModel
    }): ?string => {
        const {
            volume: { unit }
        } = entity;
        return EntityImportDataHelper.validateVolumeUnit(unit);
    };

    static getCustomFields(entitySettings) {
        return entitySettings?.vehicleModelSettings?.customFields;
    }

    static addUnitForTimeResourceLimit(
        limits: RiderPersonaLimits,
        resourceName: string
    ): RiderPersonaLimits {
        if (!DataUtils.isEmpty(limits.get(resourceName))) {
            return limits.setIn([resourceName, 'unit'], SECONDS);
        }
        return limits;
    }

    static processEntityRows({ rows }, { clientId }) {
        return rows.map(entityRow =>
            entityRow.map((cellValue, headerId) => {
                switch (headerId) {
                    case 'customProperties':
                        return cellValue.filter(value => value);
                    case 'volume':
                        return !cellValue.getIn(['unit'])
                            ? cellValue.setIn(
                                  ['unit'],
                                  VolumeLimitUnitEnum.METERS_CUBIC
                              )
                            : cellValue;
                    case 'limits': {
                        let modifiedValue = cellValue;

                        if (!cellValue.getIn(['task', 'name'])) {
                            modifiedValue = cellValue
                                .setIn(['task', 'name'], 'task')
                                .setIn(['distance', 'name'], 'distance')
                                .setIn(['travelTime', 'name'], 'travelTime')
                                .setIn(['totalTime', 'name'], 'totalTime')
                                .setIn(['location', 'name'], 'location')
                                .setIn(['volume', 'name'], 'volume')
                                .setIn(['weight', 'name'], 'weight');
                        }

                        modifiedValue = VehicleModelImportHelper.addUnitForTimeResourceLimit(
                            modifiedValue,
                            'travelTime'
                        );
                        modifiedValue = VehicleModelImportHelper.addUnitForTimeResourceLimit(
                            modifiedValue,
                            'totalTime'
                        );

                        // add weight unit
                        if (!DataUtils.isEmpty(modifiedValue.get('weight'))) {
                            modifiedValue = modifiedValue.setIn(
                                ['weight', 'unit'],
                                KG
                            );
                        }

                        // add distance unit
                        if (!DataUtils.isEmpty(modifiedValue.get('distance'))) {
                            modifiedValue = modifiedValue.setIn(
                                ['distance', 'unit'],
                                UnitEnum.METERS
                            );
                        }
                        const modifiedCustomResourceLimits = EntityImportDataHelper.getResourceLimits(
                            cellValue.get('resourceLimits')
                        );

                        modifiedValue = modifiedValue.setIn(
                            ['resourceLimits'],
                            modifiedCustomResourceLimits
                        );
                        return DataUtils.isEmpty(
                            modifiedValue.get('volume')?.get('unit')
                        )
                            ? modifiedValue.setIn(
                                  ['volume', 'unit'],
                                  VolumeLimitUnitEnum.METERS_CUBIC
                              )
                            : modifiedValue;
                    }
                    case 'transporterId':
                        return cellValue &&
                            !DataUtils.isEmpty(cellValue.get('transporterId'))
                            ? cellValue.set('clientId', clientId)
                            : null;
                    default:
                        return cellValue;
                }
            })
        );
    }

    static getUploadedEntities = ({
        uploadedTable,
        clientId,
        getDataForCell
    }) => {
        const vehicleModelsData = EntityImportHelper.makeDataForEntities({
            table: uploadedTable,
            clientId,
            getDataForCell,
            processEntityRows: VehicleModelImportHelper.processEntityRows
        });
        const vehicleModelEntities = Immutable.List(
            vehicleModelsData.map(vehicleModelEntity =>
                MasterVehicleModel.fromJS(vehicleModelEntity.toJS())
            )
        );

        return vehicleModelEntities;
    };

    static generateEntityList = (
        vehicleModelData: Array<*>
    ): Immutable.List<MasterVehicleModel> =>
        Immutable.List(
            vehicleModelData.map(vehicleModelEntity =>
                MasterVehicleModel.fromJS(vehicleModelEntity)
            )
        );
}

export default VehicleModelImportHelper;
